/* eslint-disable no-undef */
import React, { Fragment, Component } from 'react'
import i18n from 'i18next'
import { observer } from 'mobx-react'
import { reaction } from 'mobx'
import * as R from 'ramda'
import ChartSelector from '../ChartSelector'
import colours from '../Chart/colours'
import Chart from '../Chart'
import ReportPageTitle from '../ReportPageTitle'
import OptionTitle from './OptionTitle'
import OptionResponse from '../OptionResponse'
import { getQuestionTitle, getOptionTitle } from '../../../../../utils/QuestionHelper'
import Panel from '../../../../../UniformComponents/Panel'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import QuestionFooter from '../QuestionFooter'
import DebugInfo from '../DebugInfo'
import QuestionEdit from '../QuestionEdit'
import AiOverview from './AiOverview'

@observer
class Priority extends Component {
    constructor(props) {
        super(props)
        const {
            store: { survey },
            reportStore,
            question,
            dataKey,
        } = props
        this.isMaxbiReport = survey.isMaxbi()
        this.showChart = this.isMaxbiReport ? reportStore.showChart(question) : true
        this.indexArray = this.isMaxbiReport ? reportStore.filterResponses(dataKey, question)
            : Array.from(Array(question.optionSet.options.length).keys())
        this.categories = question.optionSet.options.map((option) => (
            getOptionTitle({
                langs: option.langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
        ))
        this.xOptions = Array.from(Array(question.optionSet.options.length).keys()).map((o) => (o + 1))
        this.data = reportStore.reportData.questions[dataKey]
        this.state = {
            updated: new Date().getTime(),
            sorting: {
                direction: undefined,
                sortBy: undefined,
                defaultSorting: Array.from(Array(question.optionSet.options.length).keys()),
                sortedIndexArray: this.indexArray,
            },
            chartType: R.pathOr('column', ['settings', 'report', 'graphType'], question),
        }
        this.chartColours = [
            colours.getSerieColour('survey'),
            colours.getSerieColour('comparison'),
        ]

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                this.setState({
                    updated: props.reportStore.reportDataFetched,
                })
            },
        )

        this.chartType = question.settings.report.graphType
        reportStore.questionSorting[dataKey] = this.getQuestionSorting
    }

    getQuestionSorting = () => {
        const { sorting } = this.state
        return [sorting.sortedIndexArray, this.chartType]
    }

    getXOptionArray = (data) => (
        data.xOptions
            ? Object.keys(data.xOptions)
            : []
    )

    colMapper = ({ path, type, id }, data, option, index) => {
        const {
            question, reportStore, store, reportFilter,
        } = this.props
        const { survey } = store
        let col = null
        if (type === 'pct') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                    %
                </td>
            )
        } else if (type === 'OptionResponse') {
            col = (
                <td>
                    <OptionResponse
                        id={id}
                        value={R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                        reportStore={reportStore}
                        reportFilter={reportFilter}
                    />
                </td>
            )
        } else if (type === 'optionTitle') {
            col = (
                <th>
                    <OptionTitle
                        id={option.id}
                        reportStore={reportStore}
                        option={option}
                        locale={reportStore.locale}
                        defaultLocale={survey.metadata.defaultLocale}
                        initialLocale={question.metadata.initialLocale}
                        toggleOther={this.toggleOther}
                    />
                </th>
            )
        } else if (type === 'val') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data)}
                </td>
            )
        } else if (type === 'avg') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                </td>
            )
        } else if (type === 'optionSelected') {
            if (R.pathOr(0, [...path.split('.'), index])(data) === 1) {
                col = (
                    <td>
                        <i className="fas fa-check" />
                    </td>
                )
            } else {
                col = (
                    <td />
                )
            }
        }
        return col
    }

    sortBy = (col) => {
        const sorting = R.clone(this.state.sorting)
        const {
            reportStore, question, store, dataKey,
        } = this.props
        const { survey } = store
        const data = reportStore.reportData.questions[dataKey]

        const oldSortBy = sorting.sortBy

        let values = []
        if (!col.path) {
            sorting.sortBy = 'label'
            values = question.optionSet.options.reduce((newOptions, o) => {
                newOptions.push(getOptionTitle({
                    langs: o.langs,
                    locale: reportStore.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }))
                return newOptions
            }, [])
        } else {
            sorting.sortBy = col.path
            values = R.pathOr(0, col.path.split('.'))(data)
        }
        const sortedIndexArray = Array
            .from(Array(values.length).keys())
            .sort((a, b) => (values[a] < values[b] ? -1 : values[a] > values[b] ? 1 : 0))

        if (sorting.sortBy !== oldSortBy || !sorting.direction) {
            sorting.direction = 'ASC'
        } else {
            sorting.direction = sorting.direction === 'ASC'
                ? 'DESC'
                : undefined
        }

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = sortedIndexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = sortedIndexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }

        this.setState({
            sorting,
        })
    }

    sortIcon = ({ path }) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (
            direction
            && (
                !path && sortBy === 'label'
                || path === sortBy
            )
        ) {
            const iconClass = direction === 'ASC'
                ? 'fal fa-long-arrow-up'
                : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    generateDataTable = () => {
        const {
            question,
            reportStore,
            dataKey,
        } = this.props
        const { sorting } = this.state
        const data = reportStore.reportData.questions[dataKey]

        const optionSetXColumns = this.xOptions.map((value, index) => ({
            label: value,
            id: `option_${value}`,
            path: `xOptions.${value}.numberOfResponses`,
            type: this.isMaxbiReport ? 'optionSelected' : 'val',
        }))
        const averages = [{
            label: 'Average',
            type: 'avg',
            path: 'aggregate.average',
        }]

        const cols = this.isMaxbiReport ? [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            ...optionSetXColumns,
        ] : [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            ...optionSetXColumns,
            ...averages,
        ]

        this.cols = averages

        return (
            <div style={{ overflowX: 'auto' }}>
                <table
                    className="datatable question tablesorter tablesorter-default"
                    style={{
                        width: '100%',
                    }}
                >
                    <thead>
                        <tr className="col tablesorter-headerRow">
                            {cols
                                .map((col) => (
                                    this.isMaxbiReport
                                        ? (
                                            <th className={`${col.class || ''} tablesorter-header`}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                </div>
                                            </th>
                                        ) : (
                                            <th style={{ cursor: 'pointer' }} className={`${col.class || ''} tablesorter-header`} onClick={() => this.sortBy(col)}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                    {this.sortIcon(col)}
                                                </div>
                                            </th>
                                        )
                                ))}
                            <td className="tlast" />
                        </tr>
                    </thead>
                    <tbody>
                        {sorting.sortedIndexArray.map((index, i) => {
                            const option = question.optionSet.options[index]
                            return (
                                <tr className="col" key={`option_${option.id}`}>
                                    {
                                        cols.map((col) => this.colMapper(col, data, option, index))
                                    }
                                    {
                                        Array.from(reportStore.yoyComparisons).map(([id]) => (
                                            R.hasPath(['aggregate', 'comparison', id.toLowerCase()], data)
                                                ? (
                                                    <td>
                                                        {R.pathOr(0, ['aggregate', 'comparison', id.toLowerCase(), index], data).toFixed(2)}
                                                    </td>
                                                )
                                                : null
                                        ))
                                    }
                                    <td className="tlast" />
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    getChartCategorySet = () => {
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        const { question } = this.props
        console.log('sorted categoryMap', sortedIndexArray.map((sortedIndex) => question.optionSet.options[sortedIndex]))
        return sortedIndexArray.map((sortedIndex) => question.optionSet.options[sortedIndex])
    }

    getChartCategories = () => {
        const { question, reportStore, store } = this.props
        const { survey } = store
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        return sortedIndexArray.map((sortedIndex) => (
            getOptionTitle({
                langs: question.optionSet.options[sortedIndex].langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
        ))
    }

    render() {
        const {
            question,
            store,
            reportStore,
            reportFilter,
            dataKey,
            pageId,
        } = this.props
        const { survey } = store
        const { chartType } = this.state
        const data = R.or(reportStore.reportData.questions[dataKey], {})
        return (
            <Panel cssClass="question">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={getQuestionTitle({
                                content: question.content.shortTitle,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            h3={getQuestionTitle({
                                content: question.content.longTitle,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit reportStore={reportStore} pageId={pageId} />
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                <AiOverview />
                <div id={`questionData_${question.pageQuestionId}`}>
                    {this.generateDataTable()}
                </div>
                {this.showChart && data.hasResponses && (
                    <Fragment>
                        <ChartSelector
                            chartType={chartType}
                            onChange={(chartType) => { this.setState({ chartType }) }}
                        />
                        <Chart
                            series={this.cols}
                            dataKey={dataKey}
                            colors={this.chartColours}
                            reportStore={reportStore}
                            pageQuestionId={question.pageQuestionId}
                            sortedIndexArray={this.state.sorting.sortedIndexArray}
                            question={question}
                            reportFilter={reportFilter}
                            survey={store.survey}
                            data={this.data}
                            id={question.pageQuestionId}
                            categorySet={this.getChartCategorySet()}
                            chartType={chartType}
                            questionTypeCode={question.questionTypeCode}
                            kpiType={question.metadata.kpiType}
                            categories={this.getChartCategories()}
                        />
                        <QuestionFooter
                            dataKey={dataKey}
                            reportStore={reportStore}
                        />
                    </Fragment>
                )}
            </Panel>
        )
    }
}

export default Priority
